import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import type {
  ComparePreviewRunsRequest,
  PreviewRunComparison,
} from "@/lib/previewComparison";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { classifyRunReadFailure, type RunReadFailure } from "./previewInsights";

/**
 * Preview Insights — the Compare surface's one request (#1107).
 *
 * `POST /v1/projects/{projectId}/preview-comparisons` (#1106) takes two opaque
 * run locators and answers a comparison computed for that request and dropped
 * with it. Nothing is created, nothing is stored, and — the contract is
 * explicit — the same request made twice makes two full sets of upstream reads.
 *
 * A MUTATION, not a query, and for that last reason. A query would be cached,
 * re-fetched on a remount and polled if anything ever gave it an interval; each
 * of those spends two whole-run reads on the recorded preview targets to
 * recompute an answer nobody asked for again. This fires exactly when a
 * clinician presses Compare, its result lives in the mutation's own state for
 * as long as the panel is mounted, and it is dropped with the panel. There is
 * no cache entry, no storage, and no comparison artifact anywhere — the
 * ticket's explicit non-goal.
 *
 * WHAT THE BROWSER SENDS, exhaustively: the Project id in the path and the two
 * opaque locators in the body. `ComparePreviewRunsRequest` has no other member
 * and the service refuses an unknown one, so nothing about what either run is
 * ABOUT — candidate, patient, target, revision, environment, Liv or Brain
 * session — can enter this request (ADR 0013 N6).
 *
 * `retry: false` for the reason every Preview Insights read states, and more
 * sharply here: one press is one comparison, and an automatic second attempt
 * would spend another four upstream reads on a failure the clinician has not
 * yet seen.
 */
export function useComparePreviewRunsMutation(
  projectId: string,
): UseMutationResult<PreviewRunComparison, unknown, ComparePreviewRunsRequest> {
  const client = useApiClient();
  return useMutation({
    async mutationFn(body: ComparePreviewRunsRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/preview-comparisons", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    retry: false,
  });
}

/**
 * Why a comparison could not be made.
 *
 * Every arm of `RunReadFailure` applies unchanged — the comparison resolves two
 * runs through the same chain a single run read uses, and answers the same
 * refusals — plus one this endpoint alone can give.
 *
 * `refused` is the 422 the service answers BEFORE any lookup: the request
 * named one run twice, or its shape is not the contract's. It is kept apart
 * from the generic not-found deliberately. A 422 is a statement about the
 * REQUEST and reveals nothing about whether either locator names anything, so
 * reporting it plainly creates no existence oracle; collapsing it onto "not
 * available" would instead tell a clinician who made a typo that their run had
 * disappeared.
 */
export type ComparisonFailure = RunReadFailure | { kind: "refused" };

export function classifyComparisonFailure(error: unknown): ComparisonFailure {
  if (error instanceof ApiError && (error.status === 422 || error.status === 400)) {
    return { kind: "refused" };
  }
  return classifyRunReadFailure(error);
}
